import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Building2, 
  Users, 
  Key,
  Copy,
  RefreshCw,
  UserPlus,
  Loader2,
  Check,
  Code,
  BarChart3,
  Clock,
  Zap,
  Crown,
  Eye,
  EyeOff,
  QrCode,
  Download,
  Share2,
  Printer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyPage = () => {
  const { user, token } = useAuth();
  const [company, setCompany] = useState(null);
  const [team, setTeam] = useState({ members: [], pending_invitations: [] });
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // QR Code state
  const qrRef = useRef(null);
  const [qrSize, setQrSize] = useState(256);
  const [qrColor, setQrColor] = useState('#5DBDB3'); // Teal brand color

  // Generate the company's unique URL for QR code
  const getCompanyQrUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/company/${company?.id}/scan`;
  };

  // Download QR code as PNG
  const downloadQrCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    canvas.width = qrSize * 2;
    canvas.height = qrSize * 2;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `${company?.name?.replace(/\s+/g, '_')}_QR_Code.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    toast.success('QR Code downloaded!');
  };

  // Copy QR URL to clipboard
  const copyQrUrl = () => {
    navigator.clipboard.writeText(getCompanyQrUrl());
    toast.success('Link copied to clipboard!');
  };

  // Print QR code - uses blob URL instead of document.write for security
  const printQrCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    // Sanitize company name to prevent XSS
    const safeName = (company?.name || 'Company').replace(/[<>&"']/g, (c) => ({
      '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
    }[c]));
    const safeUrl = getCompanyQrUrl().replace(/[<>&"']/g, (c) => ({
      '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
    }[c]));
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeName} - QR Code</title>
          <style>
            body { 
              display: flex; 
              flex-direction: column;
              align-items: center; 
              justify-content: center; 
              min-height: 100vh;
              margin: 0;
              font-family: system-ui, -apple-system, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 { color: #5DBDB3; margin-bottom: 10px; }
            p { color: #666; margin-bottom: 30px; }
            .url { font-size: 12px; color: #999; margin-top: 20px; word-break: break-all; max-width: 400px; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${safeName}</h1>
            <p>Scan to view ingredient transparency</p>
            ${svgData}
            <p class="url">${safeUrl}</p>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;
    
    // Use blob URL instead of document.write for better security
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, '_blank');
    
    // Clean up blob URL after window loads
    if (printWindow) {
      printWindow.onafterprint = () => URL.revokeObjectURL(blobUrl);
    }
  };

  const fetchCompanyData = useCallback(async () => {
    try {
      const [companyRes, teamRes] = await Promise.all([
        axios.get(`${API}/company/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/company/team`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCompany(companyRes.data);
      setTeam(teamRes.data);
    } catch (error) {
      console.error('Failed to fetch company data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const copyApiKey = () => {
    navigator.clipboard.writeText(company?.api_key || '');
    toast.success('API key copied to clipboard');
  };

  const regenerateApiKey = async () => {
    if (!confirm('Are you sure? This will invalidate the current API key.')) return;
    
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/company/api-key/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany({ ...company, api_key: response.data.api_key });
      toast.success('API key regenerated successfully');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    } finally {
      setRegenerating(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) return;
    
    setInviting(true);
    try {
      await axios.post(`${API}/company/team/invite`, {
        email: inviteEmail,
        name: inviteName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation sent successfully');
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteName('');
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const getPlanLimits = () => {
    const limits = {
      basic: { scans: 100, api: 500, team: 1 },
      pro: { scans: 1000, api: 5000, team: 5 },
      enterprise: { scans: -1, api: -1, team: -1 }
    };
    return limits[company?.subscription_plan] || limits.basic;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
          <h1 className="text-3xl font-bold mb-4">No Active Subscription</h1>
          <p className="text-muted-foreground mb-8">
            Subscribe to a plan to access company features, team management, and API access.
          </p>
          <Link to="/subscription">
            <Button size="lg" data-testid="subscribe-cta">
              <Crown className="w-4 h-4 mr-2" />
              View Plans
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const limits = getPlanLimits();

  return (
    <div className="min-h-screen bg-background" data-testid="company-page">
      <Navigation />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold tracking-tight" data-testid="company-name">
                {company.name}
              </h1>
              <Badge variant="outline" className="capitalize">
                {company.subscription_plan}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Manage your company, team, and API access
            </p>
          </div>
          <Link to="/subscription">
            <Button variant="outline">
              <Zap className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </Link>
        </div>

        {/* Usage Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <span className="font-medium">Scans Used</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {limits.scans === -1 ? 'Unlimited' : `${company.scans_used} / ${limits.scans}`}
                </span>
              </div>
              {limits.scans !== -1 && (
                <Progress value={(company.scans_used / limits.scans) * 100} className="h-2" />
              )}
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-purple-500" />
                  <span className="font-medium">API Calls</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {limits.api === -1 ? 'Unlimited' : `${company.api_calls_used} / ${limits.api}`}
                </span>
              </div>
              {limits.api !== -1 && (
                <Progress value={(company.api_calls_used / limits.api) * 100} className="h-2" />
              )}
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">Team Members</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {limits.team === -1 ? 'Unlimited' : `${team.members.length} / ${limits.team}`}
                </span>
              </div>
              {limits.team !== -1 && (
                <Progress value={(team.members.length / limits.team) * 100} className="h-2" />
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="qrcode" className="space-y-6">
          <TabsList>
            <TabsTrigger value="qrcode" data-testid="tab-qrcode">
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api">
              <Key className="w-4 h-4 mr-2" />
              API Access
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
          </TabsList>

          {/* QR Code Tab */}
          <TabsContent value="qrcode">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* QR Code Display */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-[#5DBDB3]" />
                    Your Company QR Code
                  </CardTitle>
                  <CardDescription>
                    Display this QR code on products, menus, or in-store for customers to scan and view ingredient transparency
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div 
                    ref={qrRef}
                    className="flex justify-center p-6 bg-white rounded-xl border border-border"
                    data-testid="qr-code-display"
                  >
                    <QRCodeSVG
                      value={getCompanyQrUrl()}
                      size={qrSize}
                      level="H"
                      includeMargin={true}
                      fgColor={qrColor}
                      bgColor="#FFFFFF"
                      imageSettings={{
                        src: "/logo192.png",
                        height: 40,
                        width: 40,
                        excavate: true,
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button onClick={downloadQrCode} data-testid="download-qr-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={copyQrUrl} data-testid="copy-qr-link-btn">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                    <Button variant="outline" onClick={printQrCode} data-testid="print-qr-btn">
                      <Printer className="w-4 h-4 mr-2" />
                      Print
                    </Button>
                  </div>

                  {/* QR URL Preview */}
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Customers will be directed to:</p>
                    <p className="text-sm font-mono break-all">{getCompanyQrUrl()}</p>
                  </div>
                </CardContent>
              </Card>

              {/* QR Code Settings & Usage */}
              <div className="space-y-6">
                <Card className="border border-border">
                  <CardHeader>
                    <CardTitle>Customize QR Code</CardTitle>
                    <CardDescription>
                      Adjust the appearance of your QR code
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="128"
                          max="512"
                          value={qrSize}
                          onChange={(e) => setQrSize(Number(e.target.value))}
                          className="flex-1"
                        />
                        <span className="text-sm text-muted-foreground w-16">{qrSize}px</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setQrColor('#5DBDB3')}
                          className={`w-8 h-8 rounded-full bg-[#5DBDB3] border-2 ${qrColor === '#5DBDB3' ? 'border-black' : 'border-transparent'}`}
                          title="Teal (Brand)"
                        />
                        <button
                          onClick={() => setQrColor('#F08B7D')}
                          className={`w-8 h-8 rounded-full bg-[#F08B7D] border-2 ${qrColor === '#F08B7D' ? 'border-black' : 'border-transparent'}`}
                          title="Coral (Brand)"
                        />
                        <button
                          onClick={() => setQrColor('#000000')}
                          className={`w-8 h-8 rounded-full bg-black border-2 ${qrColor === '#000000' ? 'border-gray-400' : 'border-transparent'}`}
                          title="Black"
                        />
                        <button
                          onClick={() => setQrColor('#1e40af')}
                          className={`w-8 h-8 rounded-full bg-blue-800 border-2 ${qrColor === '#1e40af' ? 'border-black' : 'border-transparent'}`}
                          title="Blue"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-border">
                  <CardHeader>
                    <CardTitle>How to Use</CardTitle>
                    <CardDescription>
                      Ideas for displaying your QR code
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                        <span><strong>Product Packaging:</strong> Add to your product labels so customers can scan before buying</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                        <span><strong>Restaurant Menus:</strong> Place near dishes to show ingredient transparency</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                        <span><strong>Store Displays:</strong> Print large posters for in-store scanning stations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                        <span><strong>Marketing Materials:</strong> Include in brochures, business cards, and ads</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                        <span><strong>Festival Booths:</strong> Display prominently for event attendees with allergies</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* API Tab */}
          <TabsContent value="api">
            <div className="grid gap-6">
              {/* API Key Card */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle>Your API Key</CardTitle>
                  <CardDescription>
                    Use this key to integrate ingredient analysis into your applications
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={company.api_key || ''}
                        readOnly
                        className="pr-20 font-mono"
                        data-testid="api-key-input"
                      />
                      <button
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-12 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={copyApiKey}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="copy-api-key"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={regenerateApiKey}
                      disabled={regenerating}
                      data-testid="regenerate-api-key"
                    >
                      {regenerating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Keep this key secret. Never expose it in client-side code.
                  </p>
                </CardContent>
              </Card>

              {/* API Documentation */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle>Quick Start</CardTitle>
                  <CardDescription>
                    Integrate ingredient analysis into your app in minutes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-2">Analyze Ingredients</h4>
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/v1/analyze" \\
  -d "api_key=YOUR_API_KEY" \\
  -d "ingredients_text=Sugar, Water, Natural Flavors" \\
  -d "product_name=Energy Drink"`}
                    </pre>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium mb-2">Response Example</h4>
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`{
  "product_name": "Energy Drink",
  "health_score": 45,
  "allergen_warnings": [],
  "hidden_ingredients": ["MSG (in Natural Flavors)"],
  "summary": "Contains processed ingredients...",
  "ingredients": [...]
}`}
                    </pre>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium mb-2">Check Usage</h4>
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`curl "${process.env.REACT_APP_BACKEND_URL}/api/v1/usage?api_key=YOUR_API_KEY"`}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card className="border border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>
                      Manage who has access to your company account
                    </CardDescription>
                  </div>
                  {user?.is_company_admin && (
                    <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="invite-member-btn">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Invite Member
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Invite Team Member</DialogTitle>
                          <DialogDescription>
                            Send an invitation to join your company
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleInvite} className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="invite-name">Name</Label>
                            <Input
                              id="invite-name"
                              placeholder="John Doe"
                              value={inviteName}
                              onChange={(e) => setInviteName(e.target.value)}
                              required
                              data-testid="invite-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="invite-email">Email</Label>
                            <Input
                              id="invite-email"
                              type="email"
                              placeholder="john@company.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              required
                              data-testid="invite-email-input"
                            />
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" disabled={inviting} data-testid="send-invite-btn">
                              {inviting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                'Send Invitation'
                              )}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Active Members */}
                  {team.members.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      {member.is_company_admin && (
                        <Badge variant="secondary">Admin</Badge>
                      )}
                    </div>
                  ))}

                  {/* Pending Invitations */}
                  {team.pending_invitations.length > 0 && (
                    <>
                      <Separator />
                      <h4 className="font-medium text-sm text-muted-foreground">Pending Invitations</h4>
                      {team.pending_invitations.map((invite) => (
                        <div 
                          key={invite.id} 
                          className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-dashed border-border"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                              <Clock className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{invite.name}</p>
                              <p className="text-sm text-muted-foreground">{invite.email}</p>
                            </div>
                          </div>
                          <Badge variant="outline">Pending</Badge>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CompanyPage;
